import React, { useEffect, useState } from 'react';

const LocationSearch = ({
  country,
  onCountryChange,
  query,
  onQueryChange,
  selectedLocation,
  onLocationSelect,
  error,
}) => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [lookupError, setLookupError] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const input = query.trim();
    if (input.length < 2) {
      setResults([]);
      return undefined;
    }

    const timeout = setTimeout(async () => {
      setLoading(true);
      setLookupError('');
      try {
        const payload = {
          query: country.trim() ? `${input}, ${country.trim()}` : input,
          limit: 6,
        };

        const apiBase = (process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/$/, '');
        const candidateUrls = ['/api/geocode', ...(apiBase ? [`${apiBase}/api/geocode`] : [])];

        let data = null;
        for (const url of candidateUrls) {
          try {
            const response = await fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });

            const parsed = await parseJsonResponse(response);
            if (response.ok && parsed.ok) {
              data = parsed;
              break;
            }
          } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
              console.warn('[chart/geocode] endpoint failed', url, err);
            }
          }
        }

        if (!data) {
          data = await geocodeDirectNominatim(payload.query, payload.limit);
        }

        setResults(Array.isArray(data.results) ? data.results : []);
        setOpen(true);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('[chart/geocode] lookup failed', err);
        }
        setLookupError('We couldn\'t find that location right now. Please try another city or check the spelling.');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 280);

    return () => clearTimeout(timeout);
  }, [country, query]);

  return (
    <div className="chart-location-search">
      <div className="form-group">
        <label htmlFor="chart-country">Country</label>
        <input
          id="chart-country"
          className="form-control"
          type="text"
          value={country}
          onChange={(event) => onCountryChange(event.target.value)}
          placeholder="e.g. United States"
          autoComplete="country-name"
        />
      </div>

      <div className="form-group">
        <label htmlFor="chart-location">City or place</label>
        <input
          id="chart-location"
          className="form-control"
          type="text"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onFocus={() => setOpen(true)}
          placeholder="Search your birth location"
          autoComplete="off"
          aria-expanded={open}
        />
      </div>

      {loading && <p className="chart-help">Searching locations...</p>}
      {lookupError && <p className="chart-error-text">{lookupError}</p>}
      {error && <p className="chart-error-text">{error}</p>}

      {open && results.length > 0 && (
        <ul className="chart-location-results" role="listbox">
          {results.map((result, index) => (
            <li key={`${result.lat}-${result.lng}-${index}`}>
              <button
                type="button"
                onClick={() => {
                  onLocationSelect(result);
                  onQueryChange(result.displayName);
                  setOpen(false);
                }}
              >
                <span>{result.city || result.displayName}</span>
                <small>{result.country || 'Location'}</small>
              </button>
            </li>
          ))}
        </ul>
      )}

      {selectedLocation && (
        <p className="chart-help">
          Coordinates resolved: {Number(selectedLocation.lat).toFixed(4)},{' '}
          {Number(selectedLocation.lng).toFixed(4)}
        </p>
      )}
    </div>
  );
};

async function parseJsonResponse(response) {
  const contentType = (response.headers.get('content-type') || '').toLowerCase();
  if (!contentType.includes('application/json')) {
    const snippet = (await response.text()).slice(0, 200);
    throw new Error(`non_json_response:${response.status}:${snippet}`);
  }
  return response.json();
}

async function geocodeDirectNominatim(query, limit) {
  const url = new URL('https://nominatim.openstreetmap.org/search');
  url.searchParams.set('q', query);
  url.searchParams.set('format', 'jsonv2');
  url.searchParams.set('addressdetails', '1');
  url.searchParams.set('limit', String(limit));

  const response = await fetch(url.toString(), {
    headers: { Accept: 'application/json' },
  });
  const data = await parseJsonResponse(response);

  const results = Array.isArray(data)
    ? data
      .map((result) => ({
        lat: Number(result.lat),
        lng: Number(result.lon),
        displayName: result.display_name,
        city:
          result.address?.city ||
          result.address?.town ||
          result.address?.village ||
          result.address?.state ||
          null,
        country: result.address?.country || null,
      }))
      .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng))
    : [];

  return {
    ok: true,
    results,
  };
}

export default LocationSearch;
